import "@mantine/charts/styles.css";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/dropzone/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/spotlight/styles.css";
import "@mantine/tiptap/styles.css";
import "@mdxeditor/editor/style.css";
import "intl-tel-input/styles";
import "mantine-react-table/styles.css";
import "maplibre-gl/dist/maplibre-gl.css";
import "src/main.css";
import "reflect-metadata";

import { ColorSchemeScript, Loader } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { StrictMode, Suspense, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { AuthContextProps, AuthProvider, useAuth } from "react-oidc-context";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { useOIDCStore } from "shared/stores/oidc";
import { BryxEmnsMantineThemeProvider } from "shared/utils/theme";
import { queryClient } from "src/queryClient";
import { router } from "src/router";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

const { promise, resolve } = Promise.withResolvers<AuthContextProps>();

const App = () => {
    const auth = useAuth();
    const isMobile = useIsMobile();

    useEffect(() => {
        if (auth.isLoading || !!window.interop) return;
        resolve(auth);
    }, [auth, auth.isLoading]);

    return (
        <Suspense fallback={<Loader />}>
            <Notifications position={isMobile ? "top-center" : "bottom-right"} />
            <RouterProvider router={router} context={{ auth: promise }} />
        </Suspense>
    );
};

const rootElement = document.getElementById("app")!;

if (!rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement);

    root.render(
        <StrictMode>
            <Suspense>
                <AuthProvider {...useOIDCStore.getState().config}>
                    <QueryClientProvider client={queryClient}>
                        <ColorSchemeScript defaultColorScheme="auto" />
                        <BryxEmnsMantineThemeProvider>
                            <ModalsProvider
                                modalProps={{
                                    centered: true,
                                    styles: { title: { fontWeight: 600 } },
                                    closeButtonProps: { "aria-label": "Close button" },
                                }}
                            >
                                <App />
                            </ModalsProvider>
                        </BryxEmnsMantineThemeProvider>
                    </QueryClientProvider>
                </AuthProvider>
            </Suspense>
        </StrictMode>,
    );
}
